import type { LineItem } from "~/features/cart/cart/models/CartData";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { RootState } from "~/app/store";
import { getActiveCartData } from "~/features/cart/cart/api/CartView";
import { userIsLoggedIn, userIsSalesman } from "~/domains/user/utils/roles";
import { debounce } from "lodash";
import { getStore } from "~/app/store/getStore";

async function isTrackingEnabled() {
  // track any logged in user except salesmen
  if (!userIsLoggedIn() || userIsSalesman()) {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allowed: boolean = await ((window as any)._etmcPromise ?? false);
  return allowed;
}

async function trackAbandonment(key: string, value: string | object) {
  // Sanity check - can we track?
  const allowed = await isTrackingEnabled();
  if (allowed) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)._etmc?.push([key, value]);
  }
}
export function trackPdpView(product: ProductData, category: string) {
  trackAbandonment("trackPageView", { item: product.code, category });
}

export function trackClearCart() {
  trackAbandonment("trackCart", { clear_cart: true });
}

type AbandonmentCartItem = {
  item: string;
  quantity: number;
  price?: number;
  unique_id: string;
};

export async function trackCartContents(store: RootState) {
  const allowed = await isTrackingEnabled();
  if (!allowed) {
    return;
  }
  trackCartContentsInternal(Object.values(store.cart.packageEntriesById), store.products.basic);
}

export const naiveTrackCartContents = debounce(async function naiveTrackCartContents() {
  const allowed = await isTrackingEnabled();
  if (!allowed) {
    return;
  }
  const data = await getActiveCartData();
  if (!data) {
    return;
  }
  trackCartContentsInternal(Object.values(data.packageEntriesById), getStore().getState().products.basic);
}, 5000);

function trackCartContentsInternal(lineItems: LineItem[], productsByCode: Record<string, ProductData>) {
  if (lineItems.length === 0) {
    trackClearCart();
    return;
  }
  const mapLineItem = (lineItem: LineItem): AbandonmentCartItem => {
    const product: ProductData = productsByCode[lineItem.productCode];
    return {
      item: product.sapMaterialNumber + lineItem.packCode,
      quantity: lineItem.quantity,
      price: lineItem.netPrice?.value ?? undefined,
      unique_id: product.code,
    };
  };

  const data = lineItems.slice().sort(sortLineItemsByOrderInCart).map(mapLineItem);

  trackAbandonment("trackCart", { cart: data });
}

function sortLineItemsByOrderInCart(a: LineItem, b: LineItem) {
  return (a.entryNumber ?? 0) - (b.entryNumber ?? 0);
}
